// #region IMPORT
import { posingStore } from 'stores';
import { GizmoModeType } from 'stores/posingStore';

import type PosingSystem from '../PosingSystem';
// @ts-expect-error ts(7016) : Could not find a declaration file for module
import { Quat, Vec3 } from '../ossos/ossos.min';
import MachineBase from './MachineBase';

// #endregion

const MODE_LIMIT = [
  [GizmoModeType.Translate, GizmoModeType.zTwist],
  [GizmoModeType.Translate],
  [GizmoModeType.zTwist],
];

export default class SpineMachine extends MachineBase {
  name = 'spine';
  pSystem!: PosingSystem;

  // Target points
  points = [
    { type: 'target', pos: [0.3, 0, 0] },
    { type: 'ctrl', pos: [0.6, 0, 0] },
    { type: 'target', pos: [0.9, 0, 0] },
  ];

  // Neutral axes to compute twist angle
  nAxes = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  // #region CUSTOM METHODS
  setTargetIndex(n: number): this {
    this.pSystem.poser.targets.setItems(this.points).selectedIndex = n;

    switch (n) {
      case 0: // TARGET POINT
      case 1: // CONTROL POINT
        posingStore.state.gizmoMode = GizmoModeType.Translate;
        this.pSystem.gizmo
          .resetTransform(this.points[n].pos, [0, 0, 0, 1])
          .show();
        break;
      case 2: // ROOT POINT
        posingStore.state.gizmoMode = GizmoModeType.zTwist;
        this.showTwistGizmo();
        break;
    }

    return this;
  }
  // #endregion

  // #region HELPERS

  /** Compute quaternion for twist gizmo */
  initTwist(lnk: any, initRad: number) {
    // const Debug = this.pSystem.vdebug;
    // const nOrtho = new Vec3();
    // const vv = new Vec3();
    // const jj = new Vec3(this.points[0].pos);
    // Debug.ln.reset();

    const rig = this.pSystem.poser.rig;
    const nSwing = new Vec3();
    const nTwist = new Vec3();

    // Get parent worldspace rotation
    const pQuat = rig.wpose.getWorldTransform(lnk.pindex).rot;

    // Compute bone's neutral rotation
    const nQuat = new Quat().fromMul(
      pQuat,
      rig.tpose.bones[lnk.index].local.rot,
    );
    nTwist.fromQuat(nQuat, lnk.axes.twist);
    nSwing.fromQuat(nQuat, lnk.axes.swing);
    this.nAxes = Vec3.orthogonal(nSwing, nTwist);

    // Debug.ln.push(jj, vv.fromAdd(this.nAxes[1], jj), 0x0000ff);

    // Apply initial twist around the swing axis
    const q = new Quat().fromAxisAngle(nSwing, -initRad).mul(nQuat);

    // Compute Swing Twist Direction for the bone's current twist rotation
    nTwist.fromQuat(q, lnk.axes.twist);
    nSwing.fromQuat(q, lnk.axes.swing);

    // Turn directions into Quaternion
    const axes = Vec3.orthogonal(nSwing, nTwist);
    q.fromLookDir(axes[2], axes[1]);

    // nTwist.fromQuat(q, [0, 1, 0]);
    // nSwing.fromQuat(q, [0, 0, 1]);
    // nOrtho.fromQuat(q, [1, 0, 0]);
    // Debug.ln.push(jj, vv.fromAdd(nTwist, jj), 0x0000ff);
    // Debug.ln.push(jj, vv.fromAdd(nSwing, jj), 0xff00ff);
    // Debug.ln.push(jj, vv.fromAdd(nOrtho, jj), 0x000000);
    // Debug.ln.push(jj, vv.fromAdd(this.nAxes[1], jj), 0xffff00);
    // Debug.ln.push(jj, vv.fromAdd(this.nAxes[2], jj), 0xff000);
    // Debug.ln.push(jj, vv.fromAdd(axes[1], jj), 0xffff00);
    // Debug.ln.push(jj, vv.fromAdd(axes[2], jj), 0xff0000);
    // Debug.ln.push(jj, vv.fromAdd(axes[0], jj), 0x000000);
    // Debug.ln.push(jj, vv.fromAdd(nTwist, jj), 0x000000);

    return q;
  }

  /** Compute twist angle from gizmo quaternion output */
  calcTwist(twist: Array<number>): number {
    const sign = Math.sign(Vec3.dot(twist, this.nAxes[0]));
    const d = Vec3.dot(twist, this.nAxes[1]);
    const c = Vec3.cross(twist, this.nAxes[1]);
    return Math.atan2(Vec3.len(c), d) * sign;
  }

  computePoints() {
    const rig = this.pSystem.poser.rig;
    const chain = rig.spine.chain;

    // Target Point
    this.points[0].pos = rig.wpose.getWorldTransform(
      chain.links.at(-1).index,
    ).pos;

    // Control Point
    this.points[1].pos = rig.getPolePos('spine');

    // Root Point
    this.points[2].pos = rig.wpose.getWorldTransform(chain.links[0].index).pos;

    return this;
  }

  showTwistGizmo() {
    const tIdx = this.pSystem.poser.targets.selectedIndex;
    const rig = this.pSystem.poser.rig;
    const chain = rig['spine'].chain;

    let lnk;
    let rad = 0;
    switch (tIdx) {
      case 0: // Target Point
        lnk = chain.links.at(-1);
        rad = rig['spine'].target.twistRadB;
        break;
      case 2: // Root Point
        lnk = chain.links[0];
        rad = rig['spine'].target.twistRadA;
        break;
    }

    if (lnk) {
      const rot = this.initTwist(lnk, rad);
      this.pSystem.gizmo.resetTransform(this.points[tIdx].pos, rot).show();
    }
  }

  updateTargets(incRender = false) {
    this.pSystem.poser.targets.setItems(this.points);
    if (incRender) this.pSystem.poser.targets.render();
    return this;
  }

  // #endregion

  // #region STATE MACHINE OVERRIDES
  onInit(ps: PosingSystem) {
    this.pSystem = ps;

    // Render Points
    this.computePoints().setTargetIndex(0);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Start Gizmo
    posingStore.state.gizmoMode = GizmoModeType.Translate;
    ps.gizmo.resetTransform(this.points[0].pos, [0, 0, 0, 1]).show();
  }

  onRelease(): void {
    this.pSystem.poser.targets.clear();
  }
  // #endregion

  // #region GIZMO EVENT OVERRIDES
  onGizmoDragStart(): void {
    this.pSystem.poser.selectors.clear();
  }

  onGizmoDragEnd(): void {
    this.pSystem.poser.refresh();
    this.computePoints().updateTargets(true);
  }

  onGizmoTranslate(v: Array<number>): void {
    // Do not allow translate to move INDEX 2 - Root Point
    switch (this.pSystem.poser.targets.selectedIndex) {
      case 0: // Target Point
        this.points[0].pos = v;
        this.pSystem.poser.rig.setTargetPos('spine', v);
        this.pSystem.poser.resolve();
        break;
      case 1: // Control Point
        this.points[1].pos = v;
        this.pSystem.poser.rig.setPolePos('spine', v);
        this.pSystem.poser.resolve();
        break;
    }
  }

  onGizmoRotate(q: Array<number>): void {
    if (posingStore.state.gizmoMode === GizmoModeType.zTwist) {
      const cTwist = new Vec3().fromQuat(q, [0, 1, 0]);
      const rad = this.calcTwist(cTwist);
      // console.log(rad * (180 / Math.PI)); // view in degrees

      switch (this.pSystem.poser.targets.selectedIndex) {
        case 0:
          this.pSystem.poser.rig.spine.target.setEndTwist(rad);
          break;

        case 2:
          this.pSystem.poser.rig.spine.target.setStartTwist(rad);
          break;
      }

      this.pSystem.poser.resolve();

      // const Debug = this.pSystem.vdebug;
      // Debug.ln.reset();
      // const vv = new Vec3();
      // const jj = new Vec3(this.points[0].pos);
      // Debug.ln.push(jj, vv.fromAdd(cTwist, jj), 0x0000ff);
      // Debug.ln.push(jj, vv.fromAdd(this.nAxes[1], jj), 0x000000);
    }
  }

  onGizmoModeChange(mode: string): void {
    // Limit gizmo change
    const tIdx = this.pSystem.poser.targets.selectedIndex;
    if (!MODE_LIMIT[tIdx].includes(mode as any)) {
      posingStore.state.gizmoMode = MODE_LIMIT[tIdx][0];
      return;
    }

    this.initGizmo();
  }
  // #endregion

  // #region HISTORY EVENT OVERRIDES
  onUndoRedo() {
    this.initGizmo();
  }
  // #endregion

  // #region MISC

  /** Initialize the gizmo's starting position & rotation */
  initGizmo() {
    switch (posingStore.state.gizmoMode) {
      case GizmoModeType.zTwist:
        this.computePoints().updateTargets(true).showTwistGizmo();
        break;

      case GizmoModeType.Translate: {
        const tIdx = this.pSystem.poser.targets.selectedIndex;
        this.computePoints().updateTargets(true);
        this.pSystem.gizmo
          .resetTransform(this.points[tIdx].pos, [0, 0, 0, 1])
          .show();
        break;
      }
    }
  }

  // #endregion
}
